import React, { ReactNode, useMemo, useState } from "react";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";

import { Option } from "../../../types/Option";

import { useVirtualize } from "../../../hooks/useVirtualize";

import { cn } from "../../../lib/utils";

import { SCROLL_STYLES } from "../../../lib/styles";

import { Checkbox } from "../../atoms/Checkbox";
import { InputText } from "../../atoms/InputText";
import { Text } from "../../atoms/Text";

// Default filter function: case-insensitive label search
const defaultFilterFunction = <T,>(option: Option<T>, search: string): boolean => {
	const labelMatch = typeof option.label === "string" && option.label.toLowerCase().includes(search.toLowerCase());
	const subtextMatch =
		option.subtext && typeof option.subtext === "string"
			? option.subtext.toLowerCase().includes(search.toLowerCase())
			: false;
	return labelMatch || subtextMatch;
};

// Default option renderer with checkered rows
const defaultRenderOption = <T,>(option: Option<T>, selected: boolean, onToggle: () => void) => {
	const optionId = `option-${JSON.stringify(option.value)}`;

	return (
		<div className="flex min-w-0 items-center gap-2 px-3 py-2">
			<Checkbox id={optionId} checked={selected} onCheckedChange={onToggle} />
			<div className="flex min-w-0 flex-1 items-center gap-1">
				<Text
					size="paragraph-3"
					weight="regular"
					color="neutral-9"
					overrideElement="label"
					htmlFor={optionId}
					wrap={false}
					dangerouslySetClassName="min-w-0 cursor-pointer truncate"
				>
					{typeof option.label === "string" ? option.label : String(option.value)}
				</Text>
				{option.subtext && (
					<Text
						size="paragraph-3"
						weight="regular"
						color="neutral-6"
						overrideElement="span"
						wrap={false}
						dangerouslySetClassName="shrink-0"
					>
						({option.subtext})
					</Text>
				)}
			</div>
		</div>
	);
};

export type MultipleSelectListProps<T = unknown> = {
	/** Array of options to display */
	options: Option<T>[];
	/** Currently selected values array */
	value: T[];
	/** Function called when selection changes */
	onChange: (selectedValues: T[], selectedOptions: Option<T>[]) => void;
	/** Placeholder text for search input */
	searchPlaceholder?: string;
	/**
	 * Controlled search value. When provided, it delegates search state management to the parent.
	 * Use this for server-side filtering.
	 */
	searchValue?: string;
	/**
	 * Callback when search value changes. Required when using controlled search (searchValue).
	 * Parent should handle debouncing and API calls for server-side filtering.
	 */
	onSearchChange?: (searchValue: string) => void;
	/**
	 * Custom function to filter options (default: filters by label).
	 * Note: This is only used for client-side filtering when searchValue is NOT provided.
	 * For server-side filtering, provide pre-filtered options from the parent.
	 */
	filterFunction?: (option: Option<T>, searchTerm: string) => boolean;
	/**
	 * Custom render function for displaying options in the list.
	 * @param option - The option to render
	 * @param isSelected - Whether this option is currently selected
	 * @param onToggle - Function to toggle selection for this option
	 * @returns React node to render for the option
	 */
	renderOption?: (option: Option<T>, isSelected: boolean, onToggle: () => void) => ReactNode;
	/**
	 * Configuration for virtualized rendering to efficiently handle large datasets.
	 */
	virtualizationConfig?: {
		/**
		 * Height of each virtualized item in pixels.
		 * @default 32
		 */
		itemHeight?: number;
		/**
		 * Maximum height of the list container in pixels.
		 * @default 320
		 */
		maxHeight?: number;
		/**
		 * Scroll event handler for infinite scrolling
		 */
		onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
	};
	/**
	 * Configuration for "Select All" checkbox functionality
	 */
	selectAllConfig?: {
		/** Whether the checkbox is checked */
		checked: boolean;
		/** Whether the checkbox is in indeterminate state */
		indeterminate?: boolean;
		/** Called when the checkbox is toggled */
		onToggle: () => void;
		/** Label text for the select all option */
		label: string;
	};
};

/**
 * A reusable multi-select list component with search functionality and checkered rows.
 * Supports virtualization for efficient rendering of large datasets.
 *
 * Supports both controlled and uncontrolled search modes:
 * - Uncontrolled (client-side filtering): Don't pass searchValue/onSearchChange
 * - Controlled (server-side filtering): Pass searchValue and onSearchChange, provide pre-filtered options
 *
 * Selection state is always controlled via value/onChange props.
 *
 */
export const MultipleSelectList = <T,>({
	options,
	value,
	onChange,
	searchPlaceholder = "Search...",
	searchValue: controlledSearchValue,
	onSearchChange,
	filterFunction = defaultFilterFunction,
	renderOption = defaultRenderOption,
	virtualizationConfig,
	selectAllConfig,
}: MultipleSelectListProps<T>) => {
	const [internalSearchTerm, setInternalSearchTerm] = useState("");

	// Determine if we're in controlled or uncontrolled mode
	const isControlled = controlledSearchValue !== undefined;
	const searchTerm = isControlled ? controlledSearchValue : internalSearchTerm;

	// Handle search changes
	const handleSearchChange = (value: string) => {
		if (isControlled) {
			// Controlled mode: delegate to parent
			onSearchChange?.(value);
		} else {
			// Uncontrolled mode: manage internally
			setInternalSearchTerm(value);
		}
	};

	// Filter options based on search term
	// Note: For server-side filtering (controlled mode), parent should provide pre-filtered options
	// and this filtering will be skipped (or act as additional client-side filtering)
	const filteredOptions = useMemo(() => {
		// If controlled mode, assume parent provides filtered options
		if (isControlled) {
			return options;
		}
		// Uncontrolled mode: filter client-side
		if (!searchTerm.trim()) return options;
		return options.filter((option) => filterFunction(option, searchTerm));
	}, [options, searchTerm, filterFunction, isControlled]);

	// Setup virtualization
	const itemHeight = virtualizationConfig?.itemHeight ?? 32;
	const maxHeight = virtualizationConfig?.maxHeight ?? 320;

	const { parentRef, virtualizer, containerHeight } = useVirtualize({
		items: filteredOptions,
		itemHeight,
		maxHeight,
		enabled: true,
		autoForceMeasure: true,
		isOpen: true,
		onItemSelect: (option: Option<T>) => {
			toggleSelection(option.value);
		},
	});

	// Toggle selection
	const toggleSelection = (optionValue: T) => {
		const isSelected = value.includes(optionValue);
		const newValues = isSelected ? value.filter((v) => v !== optionValue) : [...value, optionValue];

		const selectedOptions = options.filter((option) => newValues.includes(option.value));

		onChange(newValues, selectedOptions);
	};

	// Check if option is selected
	const isSelected = (optionValue: T) => {
		return value.includes(optionValue);
	};

	return (
		<div className="flex flex-col">
			{/* Search Input */}
			<div className="mb-4">
				<InputText
					value={searchTerm}
					onChange={handleSearchChange}
					placeholder={searchPlaceholder}
					leftIcon={faMagnifyingGlass}
					clearable={searchTerm.length > 0}
				/>
			</div>

			{/* Select All Option */}
			{selectAllConfig && filteredOptions.length > 0 && (
				<div className="mb-2 flex items-center gap-2 px-3 py-2">
					<Checkbox
						id="select-all"
						checked={selectAllConfig.indeterminate ? false : selectAllConfig.checked}
						onCheckedChange={selectAllConfig.onToggle}
					/>
					<Text
						size="paragraph-3"
						weight="semi-bold"
						color="neutral-9"
						overrideElement="label"
						htmlFor="select-all"
						dangerouslySetClassName="cursor-pointer"
					>
						{selectAllConfig.label}
					</Text>
				</div>
			)}

			{/* Options List */}
			{filteredOptions.length === 0 ? (
				<div className="py-8 text-center text-paragraph-3 text-neutral-6">
					{searchTerm ? "No results found" : "No options available"}
				</div>
			) : (
				<div
					ref={parentRef}
					className="overflow-x-hidden overflow-y-auto focus:outline-hidden"
					style={{
						...SCROLL_STYLES,
						height: `${containerHeight}px`,
						minHeight: `${containerHeight}px`,
					}}
					role="listbox"
					tabIndex={0}
					onScroll={virtualizationConfig?.onScroll}
				>
					<div
						style={{
							height: `${virtualizer.getTotalSize()}px`,
							width: "100%",
							position: "relative",
						}}
					>
						{virtualizer
							.getVirtualItems()
							.map((virtualItem: { index: number; size: number; start: number }) => {
								const option = filteredOptions[virtualItem.index];
								if (!option) return null;

								const selected = isSelected(option.value);

								return (
									<div
										key={virtualItem.index}
										className={cn(
											"absolute top-0 left-0 w-full",
											virtualItem.index % 2 === 0 ? "bg-neutral-1" : "bg-neutral-0"
										)}
										style={{
											height: `${virtualItem.size}px`,
											transform: `translateY(${virtualItem.start}px)`,
										}}
									>
										{renderOption(option, selected, () => toggleSelection(option.value))}
									</div>
								);
							})}
					</div>
				</div>
			)}
		</div>
	);
};
